(() => {
  if (customElements.get("cart-drawer")) return;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.onClose = this.onClose.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onCartIconClick = this.onCartIconClick.bind(this);
      this.onCartIconKeydown = this.onCartIconKeydown.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      this.classList.remove("active", "animate");
      this.setEventListeners();
      this.setHeaderCartIconAccessibility();
      window.addEventListener("pageshow", this.onPageShow);
    }

    disconnectedCallback() {
      this.removeEventListeners();
      window.removeEventListener("pageshow", this.onPageShow);
    }

    onPageShow(event) {
      if (!event.persisted) return;
      this.classList.remove("active", "animate");
      document.body.classList.remove("overflow-hidden");
    }

    setEventListeners() {
      this.addEventListener("click", this.onClose);
      this.addEventListener("keyup", this.onKeyUp);
    }

    removeEventListeners() {
      this.removeEventListener("click", this.onClose);
      this.removeEventListener("keyup", this.onKeyUp);
      const cartIcon = document.querySelector("#cart-icon-bubble");
      if (cartIcon) {
        cartIcon.removeEventListener("click", this.onCartIconClick);
        cartIcon.removeEventListener("keydown", this.onCartIconKeydown);
      }
    }

    onClose(e) {
      if (e.target.id === "CartDrawer-Overlay" || e.target.closest(".js-drawer-close")) this.close();
    }

    onKeyUp(e) {
      if (e.code === "Escape") this.close();
    }

    onCartIconClick(e) {
      e.preventDefault();
      this.open(this.cartIcon);
    }

    onCartIconKeydown(e) {
      if (e.code.toUpperCase() === "SPACE") {
        e.preventDefault();
        this.open(this.cartIcon);
      }
    }

    onTransitionEnd() {
      const focusTarget = this.classList.contains("is-empty")
        ? this.querySelector(".js-drawer-inner-empty")
        : document.getElementById("CartDrawer");
      const focusTrap = this.querySelector(".js-drawer-inner") || this.querySelector(".js-drawer-close");
      trapFocus(focusTarget, focusTrap);
    }

    setHeaderCartIconAccessibility() {
      this.cartIcon = document.querySelector("#cart-icon-bubble");
      if (!this.cartIcon) return;
      this.cartIcon.setAttribute("role", "button");
      this.cartIcon.setAttribute("aria-haspopup", "dialog");
      this.cartIcon.addEventListener("click", this.onCartIconClick);
      this.cartIcon.addEventListener("keydown", this.onCartIconKeydown);
    }

    open(opener) {
      if (opener) this.setActiveElement(opener);

      this.style.removeProperty("visibility");

      const summary = this.querySelector('[id^="Details-"] summary');
      if (summary && !summary.hasAttribute("role")) this.setSummaryAccessibility(summary);

      setTimeout(() => this.classList.add("animate", "active"));

      this.addEventListener("transitionend", this.onTransitionEnd, { once: true });

      document.body.classList.add("overflow-hidden");
    }

    close() {
      this.classList.remove("active");
      removeTrapFocus(this.activeElement);
      document.body.classList.remove("overflow-hidden");
    }

    setSummaryAccessibility(summary) {
      summary.setAttribute("role", "button");
      summary.setAttribute("aria-expanded", "false");
      if (summary.nextElementSibling?.getAttribute("id")) {
        summary.setAttribute("aria-controls", summary.nextElementSibling.id);
      }
      summary.addEventListener("click", (e) => {
        e.currentTarget.setAttribute(
          "aria-expanded",
          !e.currentTarget.closest("details").hasAttribute("open")
        );
      });
      summary.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    renderContents(data) {
      const pathname = window.location.pathname;
      const pathSegment = pathname.substring(pathname.lastIndexOf("/") + 1);
      const offerPageHandles = ["brain-focus-offer", "brain-protect", "sleep-well-offer", "stress-relief-offer", "vegan-omega-3-offer"];
      const isOfferPage = pathname.includes("/pages/") && offerPageHandles.includes(pathSegment);

      if (isOfferPage) {
        window.location.href = "/checkout";
        return;
      }

      this.classList.remove("is-empty");
      const inner = this.querySelector(".js-drawer-inner");
      if (inner?.classList.contains("is-empty")) inner.classList.remove("is-empty");

      this.productId = data.id;

      this.getSectionsToRender().forEach((sectionConfig) => {
        const sectionHtml = data.sections?.[sectionConfig.id];
        if (sectionHtml == null) return;
        const container = sectionConfig.selector
          ? document.querySelector(sectionConfig.selector)
          : document.getElementById(sectionConfig.id);
        if (container) {
          container.innerHTML = this.getSectionInnerHTML(
            sectionHtml,
            sectionConfig.selector
          );
        }
      });

      this.setUpdating(false);
      setTimeout(() => this.open(), 0);
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
      if (html == null || typeof html !== "string") return "";
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML ?? "";
    }

    getSectionsToRender() {
      return [
        { id: "cart-drawer", selector: "#CartDrawer" },
        { id: "cart-icon-bubble" },
      ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    setUpdating(updating) {
      if (updating) {
        this.classList.add("is-updating");
        const overlay = this.querySelector(".js-drawer-updating");
        if (overlay) overlay.setAttribute("aria-busy", "true");
      } else {
        this.classList.remove("is-updating");
        const overlay = this.querySelector(".js-drawer-updating");
        if (overlay) overlay.setAttribute("aria-busy", "false");
      }
    }
  }

  customElements.define("cart-drawer", CartDrawer);

  if (customElements.get("cart-drawer-items")) return;

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: "CartDrawer", section: "cart-drawer", selector: ".js-drawer-header" },
        { id: "CartDrawer", section: "cart-drawer", selector: ".js-drawer-inner" },
        { id: "CartDrawer", section: "cart-drawer", selector: ".js-drawer-footer" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);

  function isCartAddForm(form) {
    const action = (form.getAttribute("action") || form.action || "").toString();
    return action.indexOf("cart/add") !== -1;
  }

  function isHandledByComponent(form) {
    return form.closest("product-form") || form.closest("product-new-form");
  }

  function isCollectionPage() {
    return (
      typeof document.body !== "undefined" &&
      document.body.classList &&
      [].some.call(
        document.body.classList,
        (c) => c && c.indexOf("template-collection") === 0
      )
    );
  }

  function buildFormDataFromCollectionForm(form, cart) {
    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput?.value?.trim();
    if (!variantId) return null;
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const sellingPlanId = sellingPlanInput?.value?.trim();
    if (sellingPlanId) {
      formData.append("selling_plan", sellingPlanId);
    }
    if (cart && typeof cart.getSectionsToRender === "function") {
      const sections = cart.getSectionsToRender();
      formData.append(
        "sections",
        sections.map((s) => s.id).join(",")
      );
      formData.append("sections_url", window.location.pathname);
    }
    return formData;
  }

  function onCartAddFormSubmit(event) {
    const form = event.target?.closest?.("form");
    if (!form || !isCartAddForm(form)) return;
    if (isHandledByComponent(form) && !isCollectionPage()) return;
    if (form.dataset.cartSubmitting === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    form.dataset.cartSubmitting = "true";

    const cart =
      document.querySelector("cart-drawer") ||
      document.querySelector("cart-notification");
    if (cart && typeof cart.open === "function") {
      cart.open();
      if (typeof cart.setUpdating === "function") cart.setUpdating(true);
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.classList) submitButton.classList.add("loading");
    }

    const fetchOptions =
      typeof fetchConfig !== "undefined"
        ? fetchConfig("javascript")
        : { method: "POST", headers: {} };
    fetchOptions.headers["X-Requested-With"] = "XMLHttpRequest";
    delete fetchOptions.headers["Content-Type"];

    const isCollectionForm = form.closest(".add-to-cart-cover");
    const isQuickAddOnCollection =
      isCollectionPage() && form.closest("product-form");
    const useMinimalPayload = isCollectionForm || isQuickAddOnCollection;
    let formData;
    if (useMinimalPayload) {
      formData = buildFormDataFromCollectionForm(form, cart);
      if (!formData) {
        delete form.dataset.cartSubmitting;
        return;
      }
      if (cart && typeof cart.setActiveElement === "function") {
        cart.setActiveElement(document.activeElement);
      }
    } else {
      formData = new FormData(form);
      if (form.closest(".our-products")) {
        formData.set("quantity", "1");
      }
      if (cart && typeof cart.getSectionsToRender === "function") {
        const sections = cart.getSectionsToRender();
        formData.append(
          "sections",
          sections.map((s) => s.id).join(",")
        );
        formData.append("sections_url", window.location.pathname);
        if (typeof cart.setActiveElement === "function") {
          cart.setActiveElement(document.activeElement);
        }
      }
    }
    fetchOptions.body = formData;

    let url = window.routes?.cart_add_url || "/cart/add";
    if (url.indexOf(".js") === -1) {
      url = url.split("?")[0].replace(/\/$/, "") + ".js";
    }

    fetch(url, fetchOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          if (data.description) console.error(data.description);
          return;
        }
        if (!cart) {
          const retry = () => {
            const c =
              document.querySelector("cart-drawer") ||
              document.querySelector("cart-notification");
            if (c && typeof c.renderContents === "function") {
              c.renderContents(data);
              return;
            }
            if (window.routes?.cart_url) {
              window.location = window.routes.cart_url;
            }
          };
          setTimeout(retry, 150);
          return;
        }
        if (typeof cart.renderContents === "function") {
          cart.renderContents(data);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        delete form.dataset.cartSubmitting;
        if (cart && typeof cart.setUpdating === "function") {
          cart.setUpdating(false);
        }
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.classList) submitButton.classList.remove("loading");
        }
        const card =
          form.closest("li") ||
          form.closest(".grid__item") ||
          form.closest(".grid-view-item");
        const atcBtn = card?.querySelector(".adttoCartbtn_click, .custm_click");
        if (atcBtn) atcBtn.disabled = false;
      });
  }

  document.addEventListener("submit", onCartAddFormSubmit, true);

  function onOurProductsAddClick(event) {
    const button = event.target.closest('form[action*="cart/add"] button[type="submit"]');
    if (!button) return;
    const form = button.closest("form");
    if (!form || !form.closest(".our-products") || !isCartAddForm(form)) return;
    event.preventDefault();
    event.stopPropagation();
    if (form.dataset.cartSubmitting === "true") return;
    form.requestSubmit();
  }

  document.addEventListener("click", onOurProductsAddClick, true);

  function onCustmClick(event) {
    const button = event.target.closest(".adttoCartbtn_click, .custm_click");
    if (!button || button.disabled) return;
    const card =
      button.closest("li") ||
      button.closest(".grid__item") ||
      button.closest(".grid-view-item") ||
      button.closest(".product-card");
    if (!card) return;
    const productFormBtn = card.querySelector(".product-form__buttons button");
    const hasActive = card.querySelector(".ms-atc-info.active") || card.querySelector(".ms-onetime-box.active");
    if (productFormBtn && hasActive) return;
    let cartForm = card.querySelector(".add-to-cart-cover form");
    if (!cartForm) {
      const forms = card.querySelectorAll('form[action*="cart/add"]');
      forms.forEach((form) => {
        if (!form.closest("product-form")) cartForm = form;
      });
    }
    if (!cartForm || typeof cartForm.requestSubmit !== "function") return;
    event.preventDefault();
    event.stopPropagation();
    const qtyInput = cartForm.querySelector('input[name="quantity"]');
    if (qtyInput && parseInt(qtyInput.value, 10) < 1) qtyInput.value = 1;
    button.disabled = true;
    cartForm.requestSubmit();
  }

  document.addEventListener("click", onCustmClick, true);
})();
